import {useIssueState} from '@github-ui/issue-pr-state'
import type {IssueStateReasonType, IssueStateType} from '@github-ui/issue-pr-state/constants'
import {issueHovercardPath, issuePath} from '@github-ui/paths'
import {ChevronDownIcon} from '@primer/octicons-react'
import {IconButton, Label, Link, Spinner, Stack} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'

import styles from './SimilarIssues.module.css'

export type SimilarIssue = {
  id: string
  number: number
  title: string
  state: IssueStateType
  stateReason: IssueStateReasonType
}

type SimilarIssuesProps = {
  items: SimilarIssue[]
  isLoading: boolean
  isError: boolean
  onExpandedChange?: (expanded: boolean) => void
  repoName: string
  repoOwner: string
  /** When provided, clicking an issue will call this handler to open the side panel.
   * Cmd/Ctrl+click still opens the issue in a new tab. */
  onIssueClick?: (issue: SimilarIssue) => void
}

const IssueIcon = ({state, stateReason}: {state: IssueStateType; stateReason: IssueStateReasonType}) => {
  const {sourceIcon} = useIssueState({state, stateReason})
  const Icon = sourceIcon('Issue')
  // eslint-disable-next-line react-hooks/static-components
  return <Icon />
}

export function SimilarIssues({
  items,
  isLoading,
  isError,
  onExpandedChange,
  repoOwner,
  repoName,
  onIssueClick,
}: SimilarIssuesProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleToggle = useCallback(() => {
    const willExpand = !isExpanded
    setIsExpanded(willExpand)
    onExpandedChange?.(willExpand)
  }, [isExpanded, onExpandedChange])

  return (
    <div className={styles.wrapper}>
      <section className={styles.container} aria-labelledby="similar-issues-heading">
        <Stack direction="horizontal" align="center" justify="space-between">
          <Stack direction="horizontal" align="center" gap="condensed">
            <IconButton
              aria-label={isExpanded ? 'Collapse similar issues' : 'Expand similar issues'}
              aria-expanded={isExpanded}
              aria-controls="similar-issues-content"
              icon={ChevronDownIcon}
              variant="invisible"
              size="small"
              onClick={handleToggle}
              className={clsx(styles.chevronButton, isExpanded && styles.chevronExpanded)}
            />
            <h3 id="similar-issues-heading" className={styles.heading}>
              Similar issues
              <span className={clsx(styles.count, !isExpanded && styles.countHidden)}>({items.length})</span>
            </h3>
            {isExpanded && isLoading && <Spinner size="small" srText="Searching for similar issues" />}
          </Stack>
          <Stack direction="horizontal" align="center" gap="condensed">
            <Label size="small" variant="secondary">
              Experimental
            </Label>
            <span aria-hidden="true">·</span>
            <Link
              aria-label="Give feedback (opens new tab)"
              href="https://gh.io/duplicate-issue-feedback"
              target="_blank"
              className={styles.feedbackLink}
            >
              Give feedback
            </Link>
          </Stack>
        </Stack>
        {isExpanded && (
          <div id="similar-issues-content" className={styles.content}>
            {isError && (
              <InlineMessage className={styles.emptyState} size="small" variant="unavailable">
                Unable to load similar issues.
              </InlineMessage>
            )}
            {!isError && items.length === 0 && <p className={styles.emptyState}>No similar issues found.</p>}
            {items.length > 0 && (
              <ul className={styles.list}>
                {items.map(item => {
                  const hovercardUrl = issueHovercardPath({
                    owner: repoOwner,
                    repo: repoName,
                    issueNumber: item.number,
                  })

                  return (
                    <li key={item.id} className={styles.item}>
                      <IssueIcon state={item.state} stateReason={item.stateReason} />
                      <Link
                        href={issuePath({owner: repoOwner, repo: repoName, issueNumber: item.number})}
                        data-hovercard-url={hovercardUrl}
                        target={onIssueClick ? undefined : '_blank'}
                        onClick={
                          onIssueClick
                            ? (e: React.MouseEvent<HTMLAnchorElement>) => {
                                // Allow cmd/ctrl+click and middle-click to open in new tab
                                // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                                if (e.button === 1 || e.shiftKey || e.ctrlKey || e.metaKey) return
                                e.preventDefault()
                                onIssueClick(item)
                              }
                            : undefined
                        }
                      >
                        {item.title} <span className={styles.number}>#{item.number}</span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </section>
    </div>
  )
}
